import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { adminService } from '../../services/adminService'
import AdminLayout from '../../components/admin/AdminLayout'
import { devError } from '../../utils/devLog'
import Button from '../../components/ui/Button'
import Select from '../../components/ui/Select'

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'reviewing', label: 'Reviewing' },
  { value: 'editing', label: 'Editing' },
  { value: 'completed', label: 'Completed' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
]

const STATUS_COLORS = {
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  reviewing: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  editing: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  completed: 'bg-green-500/20 text-green-400 border-green-500/30',
  delivered: 'bg-pitch-green/20 text-pitch-green border-pitch-green/30',
  cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
}

export default function AdminSubmissions() {
  const [submissions, setSubmissions] = useState([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState(null)
  const [selectedSubmission, setSelectedSubmission] = useState(null)
  const [updating, setUpdating] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const navigate = useNavigate()

  // Debounced search: one effect depends on statusFilter, page, and debouncedSearch to avoid duplicate fetches
  const [debouncedSearch, setDebouncedSearch] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchQuery.trim()
      setDebouncedSearch(trimmed)
      setPage((p) => (trimmed ? 1 : p)) // reset to page 1 when user typed a search
    }, 500)
    return () => clearTimeout(timer)
  }, [searchQuery])

  useEffect(() => {
    fetchSubmissions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, page, debouncedSearch])

  const fetchSubmissions = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await adminService.getSubmissions({
        status: statusFilter,
        page,
        limit: 20,
        search: debouncedSearch || undefined,
      })
      if (response.success) {
        setSubmissions(response.submissions)
        setPagination(response.pagination)
      } else {
        setError(response.error || 'Failed to fetch submissions')
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch submissions. Please try again.'
      setError(errorMessage)
      devError('Error fetching submissions', err)
    } finally {
      setLoading(false)
    }
  }

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      setUpdating(true)
      setError(null)
      setSuccess(null)
      const response = await adminService.updateSubmissionStatus(id, newStatus)
      if (response.success) {
        setSuccess('Status updated successfully')
        await fetchSubmissions()
        setSelectedSubmission(null)
        setTimeout(() => setSuccess(null), 3000)
      } else {
        setError(response.error || 'Failed to update status')
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to update status. Please try again.'
      setError(errorMessage)
      devError('Error updating status', err)
    } finally {
      setUpdating(false)
    }
  }

  const handleExport = async () => {
    try {
      setExporting(true)
      setError(null)
      await adminService.exportSubmissions({
        status: statusFilter,
        search: debouncedSearch || undefined,
      })
      setSuccess('Submissions exported successfully')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      const errorMessage = err.message || 'Failed to export submissions. Please try again.'
      setError(errorMessage)
      devError('Error exporting submissions', err)
    } finally {
      setExporting(false)
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <AdminLayout>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="mb-8 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-4xl font-black mb-2">
                <span className="text-white">Submissions</span>
              </h1>
              <p className="text-gray-400">Manage all match submissions</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value)
                setPage(1)
                }}
                options={STATUS_OPTIONS}
                className="w-full md:w-48"
              />
              <Button
                onClick={handleExport}
                disabled={exporting || loading}
                loading={exporting}
                variant="outline"
                size="md"
                className="whitespace-nowrap"
              >
                {exporting ? 'Exporting...' : 'Export CSV'}
              </Button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                setPage(1)
              }}
              className="w-full bg-gray-800 border border-gray-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:border-pitch-green placeholder-gray-500"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('')
                  setPage(1)
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="bg-red-500/20 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg flex flex-wrap items-center justify-between gap-3">
              <span className="flex-1 min-w-0">{error}</span>
              <Button variant="outline" size="sm" onClick={fetchSubmissions}>
                Retry
              </Button>
            </div>
          )}
          {success && (
            <div className="bg-green-500/20 border border-green-500/30 text-green-400 px-4 py-3 rounded-lg">
              {success}
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading...</div>
        ) : submissions.length === 0 ? (
          <div className="text-center py-12 text-gray-400">No submissions found</div>
        ) : (
          <>
            <div className="space-y-4 mb-8">
              {submissions.map((submission) => (
                <motion.div
                  key={submission._id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gradient-to-br from-stadium-black to-gray-900 border-2 border-pitch-green/20 rounded-xl p-6 hover:border-pitch-green/50 transition-all"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-2">
                        <h3
                          className="text-xl font-bold text-white cursor-pointer hover:text-pitch-green"
                          onClick={() => navigate(`/admin/submissions/${submission._id}`)}
                        >
                          {submission.name}
                        </h3>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-bold border ${STATUS_COLORS[submission.status] || STATUS_COLORS.pending}`}
                        >
                          {submission.status.toUpperCase()}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-400">
                        <div>
                          <span className="text-gray-500">Email:</span> {submission.email}
                        </div>
                        <div>
                          <span className="text-gray-500">Match Type:</span> {submission.matchType}
                        </div>
                        <div>
                          <span className="text-gray-500">Focus:</span> {submission.focus}
                        </div>
                        <div>
                          <span className="text-gray-500">Submitted:</span> {formatDate(submission.createdAt)}
                        </div>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-4">
                        <button
                          type="button"
                          onClick={() => navigate(`/admin/submissions/${submission._id}`)}
                          className="text-pitch-green hover:text-neon-green text-sm font-medium"
                        >
                          View details →
                        </button>
                        {submission.driveLink && (
                          <a
                            href={submission.driveLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-pitch-green hover:text-neon-green text-sm"
                          >
                            Drive link →
                          </a>
                        )}
                        {submission.orderId && (
                          <button
                            type="button"
                            onClick={() => navigate(`/admin/orders/${submission.orderId?._id ?? submission.orderId}`)}
                            className="text-gray-400 hover:text-pitch-green text-sm"
                          >
                            Order →
                          </button>
                        )}
                      </div>
                      {submission.adminNotes && (
                        <div className="mt-2 p-2 bg-gray-800/50 rounded text-sm text-gray-300">
                          <strong>Notes:</strong> {submission.adminNotes}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <select
                        value={submission.status}
                        onChange={(e) => handleStatusUpdate(submission._id, e.target.value)}
                        disabled={updating}
                        aria-label={`Update status for ${submission.name || 'submission'}`}
                        className="bg-gray-800 border border-gray-700 text-white px-4 py-2 rounded-lg text-sm focus:outline-none focus:border-pitch-green"
                      >
                        <option value="pending">Pending</option>
                        <option value="reviewing">Reviewing</option>
                        <option value="editing">Editing</option>
                        <option value="completed">Completed</option>
                        <option value="delivered">Delivered</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
              <div className="flex items-center justify-center gap-4">
                <Button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  variant="outline"
                >
                  Previous
                </Button>
                <span className="text-gray-400">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <Button
                  onClick={() => setPage(page + 1)}
                  disabled={page === pagination.pages}
                  variant="outline"
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </motion.div>
    </AdminLayout>
  )
}
